import React from 'react';
import { v4 as uuid } from 'uuid';
import RecipientGroups from './recipientGroups';
import { RecipientList } from './recipientList';
import type { IRecipient, IGroup } from './types';

interface IProps {
  envelopeId: string;
  recipients: IRecipient[];
  optionalRecipients?: IRecipient[];
  incompletePlaceholders?: IRecipient[];
  onChange: (recipients: IRecipient[], groups: IGroup[]) => void;
  createPrimeContact?: (contact: { firstName: string; lastName: string; email: string }) => void;
  contacts: any[];
  modal?: boolean;
  disableRecipientList?: boolean;
  colorCounter?: number;
  showAutomatedRemindersFlag: boolean;
}

export function Recipients(props: IProps) {
  const { envelopeId, recipients, onChange, modal, showAutomatedRemindersFlag } = props;

  const [groups, setGroups] = React.useState<IGroup[]>([]);
  const [updatedRecipients, setUpdatedRecipients] = React.useState<IRecipient[]>(recipients);

  React.useEffect(() => {
    setUpdatedRecipients(recipients);
  }, [recipients]);

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const buildSigningGroupsFromRecipients = (newRecipients: IRecipient[]) => {
    const activeGroupIds = newRecipients.map((r) => r.group?.id).filter((i) => i);
    const sGroups = groups.filter((sg) => activeGroupIds.includes(sg.id));
    newRecipients.forEach((r) => {
      if (r.group && !sGroups.find((sg) => sg.id === r.group!.id)) {
        sGroups.push(r.group);
      }
    });
    sGroups.sort((a, b) => a.order - b.order);
    sGroups.forEach((sg, i) => {
      sg.order = i;
    });
    if (!sGroups.length) {
      sGroups.push({ id: 'default', name: 'default', order: 0 });
    }
    return sGroups;
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const setRecipients = (newRecipients: IRecipient[]) => {
    onChange(newRecipients, groups);
    setUpdatedRecipients(newRecipients);
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const setSigningGroups = (newGroups: IGroup[]) => {
    setGroups(newGroups);
    onChange(recipients, newGroups);
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const createNewGroup = () => {
    const newGroup = {
      id: uuid(),
      name: 'Signing Group',
      order: groups.length,
    };
    setSigningGroups([...groups, newGroup]);
  };

  React.useEffect(() => {
    const newGroups = buildSigningGroupsFromRecipients(recipients);
    setGroups(newGroups);
    onChange(recipients, newGroups);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [recipients]);

  const lastGroupEmpty =
    recipients.length === 0 ||
    groups.length === 0 ||
    recipients.filter((r) => r.group?.id === groups[groups.length - 1]?.id).length === 0;

  const filteredRecipients = recipients.filter((r) => (r.firstName || r.email) && r.isActive);

  return (
    <>
      <RecipientList
        envelopeId={envelopeId}
        recipients={updatedRecipients}
        incompletePlaceholders={props.incompletePlaceholders}
        optionalRecipients={props.optionalRecipients}
        signingGroups={groups}
        setRecipients={setRecipients}
        lastGroupEmpty={lastGroupEmpty}
        data-spec="recipientList"
        createPrimeContact={props.createPrimeContact}
        contacts={props.contacts}
        modal={modal}
        disableRecipientList={!!props.disableRecipientList}
        colorCounter={props.colorCounter}
        showAutomatedRemindersFlag={showAutomatedRemindersFlag}
      />
      <RecipientGroups
        recipients={filteredRecipients}
        signingGroups={groups}
        createNewGroup={createNewGroup}
        setRecipients={setRecipients}
        // modal={!!modal}
        disableRecipientList={!!props.disableRecipientList}
      />
    </>
  );
}
